import React, { useRef, useState, useEffect, useLayoutEffect, memo } from 'react';
import PropTypes from 'prop-types';
import JoditEditor from 'jodit-react';
import debounce from 'lodash.debounce';
import {
  compressImageToMaxBytes,
  estimateBytesFromDataUrl,
  MAX_INPUT_IMAGE_BYTES,
} from './imageCompression';
import './styles.scss';

function findScrollableEditorArea(container) {
  if (!container) return null;
  const walk = node => {
    if (!node) return null;
    try {
      if (node.tagName === 'IFRAME' && node.contentDocument && node.contentDocument.body) {
        const body = node.contentDocument.body;
        if (body.scrollHeight > body.clientHeight) return body;
        const inFrame = walk(body);
        if (inFrame) return inFrame;
      }
      const s = window.getComputedStyle(node);
      if (
        (s.overflowY === 'auto' || s.overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }
      for (let i = 0; i < node.children.length; i += 1) {
        const found = walk(node.children[i]);
        if (found) return found;
      }
    } catch (e) {
      // cross-origin iframe or similar
    }
    return null;
  };
  return walk(container);
}

function findEmbeddedDataUrls(html) {
  if (typeof html !== 'string') return [];
  const regex = /(data:image\/[^;]+;base64,[A-Za-z0-9+/=]+)/g;
  const out = [];
  let match;
  while ((match = regex.exec(html)) !== null) out.push(match[1]);
  return out;
}

function RichTextEditor({ initialValue = '', onChange }) {
  const editor = useRef(null);
  const editorContainerRef = useRef(null);
  const [content, setContent] = useState(initialValue);
  const contentRef = useRef(initialValue);
  const savedEditorScrollTopRef = useRef(null);

  // Sync from parent when initialValue actually changed (e.g. switching module).
  useEffect(() => {
    if (initialValue === contentRef.current) return;
    contentRef.current = initialValue;
    setContent(initialValue);
  }, [initialValue]);

  // Global paste guard: some paste flows bypass Jodit's paste event.
  useEffect(() => {
    const onPasteCapture = ev => {
      const data = ev.clipboardData;
      if (!data) return;

      const msg = 'Image is too large (max 20MB). Please use a smaller image.';

      // Block HTML/text base64 pastes > 20MB.
      try {
        const html = typeof data.getData === 'function' ? data.getData('text/html') : '';
        const text = typeof data.getData === 'function' ? data.getData('text/plain') : '';
        const candidate = html || text;
        const embedded = findEmbeddedDataUrls(candidate);
        if (embedded.length > 0) {
          const largest = embedded.reduce(
            (m, u) => Math.max(m, estimateBytesFromDataUrl(u)),
            0,
          );
          if (largest > MAX_INPUT_IMAGE_BYTES) {
            const j = editor.current;
            if (j && j.message) j.message.error(msg);
            // eslint-disable-next-line no-alert
            alert(msg);
            ev.preventDefault();
            ev.stopPropagation();
            if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
            return;
          }
        }
      } catch (e) {
        // ignore
      }

      // Block pasted image blobs > 20MB.
      if (data.items && data.items.length) {
        const items = Array.from(data.items);
        const imageItems = items.filter(
          item => item && item.type && item.type.indexOf('image') === 0,
        );
        const tooLarge = imageItems.find(item => {
          const f = item.getAsFile && item.getAsFile();
          return f && typeof f.size === 'number' && f.size > MAX_INPUT_IMAGE_BYTES;
        });
        if (tooLarge) {
          const j = editor.current;
          if (j && j.message) j.message.error(msg);
          // eslint-disable-next-line no-alert
          alert(msg);
          ev.preventDefault();
          ev.stopPropagation();
          if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        }
      }
    };

    document.addEventListener('paste', onPasteCapture, true);
    return () => document.removeEventListener('paste', onPasteCapture, true);
  }, []);

  // Guard Jodit's image dialog file input (some flows bypass uploader handler).
  useEffect(() => {
    const onChangeCapture = ev => {
      const target = ev.target;
      if (!target || target.tagName !== 'INPUT') return;
      const type = (target.getAttribute('type') || '').toLowerCase();
      if (type !== 'file') return;
      const files = target.files ? Array.from(target.files) : [];
      const imageFiles = files.filter(f => f && f.type && f.type.indexOf('image') === 0);
      if (imageFiles.length === 0) return;

      const tooLarge = imageFiles.find(f => typeof f.size === 'number' && f.size > MAX_INPUT_IMAGE_BYTES);
      if (!tooLarge) return;

      const j = editor.current;
      const msg = 'Image is too large (max 20MB). Please use a smaller image.';
      if (j && j.message) {
        j.message.error(msg);
      }
      // Some users asked explicitly for an alert.
      // eslint-disable-next-line no-alert
      alert(msg);

      // Clear selection and stop further handling for this change.
      try {
        target.value = '';
      } catch (e) {
        // ignore
      }
      ev.preventDefault();
      ev.stopPropagation();
      if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
    };

    document.addEventListener('change', onChangeCapture, true);
    return () => document.removeEventListener('change', onChangeCapture, true);
  }, []);

  const config = {
    readonly: false,
    height: 400,
    toolbar: true,
    spellcheck: true,
    language: 'en',
    toolbarButtonSize: 'medium',
    toolbarAdaptive: false,
    showCharsCounter: true,
    showWordsCounter: true,
    showXPathInStatusbar: false,
    askBeforePasteHTML: true,
    askBeforePasteFromWord: true,
    uploader: {
      insertImageAsBase64URI: true,
      handler: (request, joditEditor, files) => {
        if (files && files.length > 0 && joditEditor && joditEditor.s) {
          const imageFiles = Array.from(files).filter(file =>
            /^image\//.test(file.type),
          );
          if (imageFiles.length > 0) {
            return Promise.all(
              imageFiles.map(file =>
                compressImageToMaxBytes(file)
                  .then(compressedBase64 => {
                    joditEditor.s.insertImage(compressedBase64, null, 300);
                    return { files: [compressedBase64] };
                  })
                  .catch(err => {
                    if (joditEditor && joditEditor.message) {
                      joditEditor.message.error(
                        err.message || 'Image is too large (max 400KB). Please use a smaller image.',
                      );
                    }
                    return { files: [] };
                  }),
              ),
            ).then(() => ({ files: [] }));
          }
        }
        return Promise.resolve({ files: [] });
      },
    },
    events: {
      paste: function pasteHandler(e) {
        const ev = e.originalEvent || e;
        const data = ev.clipboardData || e.clipboardData;
        if (!data) return;

        // If the paste is HTML/text containing base64 images, block >20MB immediately.
        try {
          const html = typeof data.getData === 'function' ? data.getData('text/html') : '';
          const text = typeof data.getData === 'function' ? data.getData('text/plain') : '';
          const candidate = html || text;
          const embedded = findEmbeddedDataUrls(candidate);
          if (embedded.length > 0) {
            const largest = embedded.reduce((m, u) => Math.max(m, estimateBytesFromDataUrl(u)), 0);
            if (largest > MAX_INPUT_IMAGE_BYTES) {
              const jodit = editor.current;
              const msg = 'Image is too large (max 20MB). Please use a smaller image.';
              if (jodit && jodit.message) {
                jodit.message.error(msg);
              }
              // eslint-disable-next-line no-alert
              alert(msg);
              e.preventDefault();
              if (ev.preventDefault) ev.preventDefault();
              e.stopPropagation();
              if (ev.stopPropagation) ev.stopPropagation();
              return;
            }
          }
        } catch (err) {
          // ignore and let other handlers run
        }

        if (!data.items) return;
        const items = Array.from(data.items);
        const imageItems = items.filter(item => item.type && item.type.indexOf('image') === 0);
        if (imageItems.length === 0) return;
        e.preventDefault();
        if (ev.preventDefault) ev.preventDefault();
        e.stopPropagation();
        if (ev.stopPropagation) ev.stopPropagation();
        const jodit = editor.current;
        if (!jodit || !jodit.s || !jodit.s.insertImage) return;
        imageItems.forEach(item => {
          const blob = item.getAsFile();
          if (!blob) return;
          if (typeof blob.size === 'number' && blob.size > MAX_INPUT_IMAGE_BYTES) {
            if (jodit.message) {
              jodit.message.error(
                'Image is too large (max 20MB). Please use a smaller image.',
              );
            }
            return;
          }
          const file = new File([blob], 'pasted.png', { type: blob.type });
          compressImageToMaxBytes(file)
            .then(dataUrl => {
              jodit.s.insertImage(dataUrl, null, 300);
            })
            .catch(err => {
              if (jodit.message) {
                jodit.message.error(
                  err.message ||
                    'Pasted image is too large. Image was not inserted. Please use a smaller image.',
                );
              }
            });
        });
      },
    },
    buttons: [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'ul',
      'ol',
      '|',
      'paragraph',
      'fontsize',
      // 'brush',
      '|',
      'image',
      'video',
      'table',
      'link',
      '|',
      'align',
      '|',
      'undo',
      'redo',
    ],
  };

  const notifyParent = debounce(newContent => {
    const container = editorContainerRef.current;
    if (container) {
      const scrollEl = findScrollableEditorArea(container);
      if (scrollEl && typeof scrollEl.scrollTop === 'number') {
        savedEditorScrollTopRef.current = scrollEl.scrollTop;
      }
    }
    if (onChange) onChange(newContent);
  }, 1000);

  const handleUpdate = newContent => {
    contentRef.current = newContent;
    notifyParent(newContent);
  };

  useLayoutEffect(() => {
    const saved = savedEditorScrollTopRef.current;
    if (saved == null) return;
    savedEditorScrollTopRef.current = null;
    const container = editorContainerRef.current;
    if (!container) return;
    const scrollEl = findScrollableEditorArea(container);
    if (scrollEl && typeof scrollEl.scrollTop === 'number') {
      scrollEl.scrollTop = saved;
    }
  });

  const valueToPass =
    contentRef.current !== undefined ? contentRef.current : content;

  return (
    <div ref={editorContainerRef}>
      <JoditEditor
        ref={editor}
        value={valueToPass}
        config={config}
        onChange={handleUpdate}
      />
    </div>
  );
}

RichTextEditor.propTypes = {
  initialValue: PropTypes.string,
  onChange: PropTypes.func,
};

// Memo so we don't re-render when parent echoes back the same initialValue after paste (avoids editor scroll reset).
export default memo(RichTextEditor);
